// Shared flagged-driver logic for the Driver Manager surfaces.
//
// Extracted from driver_scorecard_dashboard.vue when the Coaching Portal
// arrived: the portal's Flagged Drivers panel and the dashboard's Flagged
// Drivers tab must agree on who is flagged and why, so the rules live here
// once and both pages import them. Pure functions only — callers own the
// memoization (the dashboard keeps its badnessPools computed; the portal
// builds pools once per fetch).
//
// Metrics checked for coaching flags. dataKey is the field name returned by
// the /tbn/driver-scorecard/all endpoint. higherBetter drives which end of
// the fleet distribution counts as "bad" for that metric.
//
// Flagging requires BOTH of these to agree a metric is genuinely bad (see
// getDriverFlags()):
//   1. Absolute gate — the value must fall in the warning/danger band of the
//      tenant's own Conditional Coloring thresholds (getFieldClass() from
//      driver_manager_thresholds.js). A value sitting in the "good" band (e.g. an
//      85 safety score against a good:80 threshold) can never flag, no matter
//      how the rest of the fleet is scoring.
//   2. Relative severity — among metrics that clear the gate, a driver in the
//      worst FLAG_BADNESS_CRITICAL_PCT / FLAG_BADNESS_WARNING_PCT percent of
//      the *active* fleet on that metric this period (metricBadnessPercentile())
//      is critical vs. warning. 'critical' additionally requires the absolute
//      band itself to be the worst (danger) band, not just warning — a value
//      that only clears the warning band can still flag, but only ever as a
//      warning, regardless of how poorly it ranks.
// Pure percentile-only flagging (no absolute gate) was tried first and
// rejected: on a fleet whose scores cluster tightly near the top, a perfectly
// fine value could still rank in the bottom 10% and flag critical. Pure
// absolute-only flagging was rejected earlier still: with ten metrics all
// flagging independently off fixed thresholds, a large fraction of the fleet
// showed up as "critical" even when performing reasonably relative to peers.
// Requiring both avoids either failure mode.
// All note() values are rounded with Math.round() rather than .toFixed(2) --
// the Flagged Drivers tab shows zero decimal places everywhere (per feedback).
// Notes state the fact only (value + how it compares to target) and stop
// there -- no prescribed action ("schedule a review", "escalate", etc.). It's
// the fleet's own call what to do with a flag, not ours to tell them.
import { getFieldClass } from './driver_manager_thresholds'

export const FLAG_METRICS = [
  { thresholdKey: 'risk_score', dataKey: 'risk_score', label: 'Safety Score', dvirGate: false, higherBetter: true,
    note: (sev, v) => sev === 'critical' ? `Safety score ${Math.round(v)} is well below target.` : `Safety score ${Math.round(v)} is below target.` },
  { thresholdKey: 'dvir_completion_rate', dataKey: 'dvir_completion_rate', label: 'DVIR Completion', dvirGate: true, higherBetter: true,
    note: (sev, v) => sev === 'critical' ? `DVIR completion is only ${Math.round(v)}%.` : `DVIR completion is at ${Math.round(v)}%, below target.` },
  { thresholdKey: 'speeding_percentage', dataKey: 'speeding_percentage', label: 'Speeding', dvirGate: false, higherBetter: false,
    note: (sev, v) => sev === 'critical' ? `Speeding on ${Math.round(v)}% of miles, well above target.` : `Speeding on ${Math.round(v)}% of miles is above target.` },
  { thresholdKey: 'distracted_driving_percentage', dataKey: 'distracted_driving_per_10k_miles', label: 'Distracted Driving', dvirGate: false, higherBetter: false,
    note: (sev, v) => sev === 'critical' ? `Distracted driving at ${Math.round(v)}/10k miles, well above target.` : `Distracted driving at ${Math.round(v)}/10k miles is above target.` },
  { thresholdKey: 'following_distance_per_10k_miles', dataKey: 'following_distance_per_10k_miles', label: 'Following Distance', dvirGate: false, higherBetter: false,
    note: (sev, v) => `Following distance violations at ${Math.round(v)}/10k miles is above target.` },
  { thresholdKey: 'harsh_braking_per_10k_miles', dataKey: 'harsh_braking_per_10k_miles', label: 'Harsh Braking', dvirGate: false, higherBetter: false,
    note: (sev, v) => `Harsh braking at ${Math.round(v)}/10k miles is above target.` },
]

// A driver in the worst 10% of the active fleet on a metric flags critical; the next
// 15% (10-25%) flags warning. Kept as named constants so the "only flag the bottom 10%"
// intent is explicit and adjustable in one place.
export const FLAG_BADNESS_CRITICAL_PCT = 90
export const FLAG_BADNESS_WARNING_PCT = 75

// The activity bars, mirroring the backend's insufficient_activity_bars()
// (driver_scorecard_processor.py / cm_scorecard_processor.py). These MUST stay
// numerically identical: the API decides who gets scored and ranked, this file
// decides who joins the flag and percentile pools, and a driver the API counts
// but this file drops is silently missing from the Flagged tab.
export const INSUFFICIENT_DATA_MILES_PER_30D = 500
export const INSUFFICIENT_DATA_BUS_DAYS_PER_30D = 5
export const INSUFFICIENT_DATA_SCALE_CAP_DAYS = 30

// Bars for a window of `rangeDays`, scaled from the per-30-day figures and
// capped at 30 so a month or longer keeps the bar it has always had. A missing
// or unparseable value means "assume a full month", matching the backend.
export function insufficientActivityBars(rangeDays) {
  const cap = INSUFFICIENT_DATA_SCALE_CAP_DAYS
  let days = Number(rangeDays)
  if (!Number.isFinite(days)) days = cap
  days = Math.max(1, Math.min(Math.trunc(days) || cap, cap))
  const scale = days / cap
  return [INSUFFICIENT_DATA_MILES_PER_30D * scale, INSUFFICIENT_DATA_BUS_DAYS_PER_30D * scale]
}

// Same activity gate used everywhere flags/percentiles are computed: excludes
// drivers with negligible data in the period. An inactive driver never flags
// and never joins the percentile pool.
//
// FI-2140: the bar now scales with the selected window, because the smallest
// selectable unit went from a month to five days. A flat 500-mile bar on a
// 5-day period asked for a month's driving in a fifth of the time -- measured
// against prod, one period on starline counted 80 drivers active on the API
// side and only 38 here, so 42 of them vanished from the flag pool AND from
// every percentile pool that decides severity. date_range_days comes down on
// the row for exactly this; when it is absent the bar falls back to a month,
// which is the pre-FI-2140 behavior.
export function isActiveDriver(d) {
  const [maxMiles, maxBusDays] = insufficientActivityBars(d.date_range_days)
  return (d.bus_days || 0) > maxBusDays || (d.miles_driven || 0) > maxMiles
}

// Pre-sorted ascending value pools per flagged metric. metricBadnessPercentile()
// is called per (driver x metric); sorting the whole fleet inside each call is
// O(drivers x metrics x N log N), so callers build the pools once per dataset
// (the dashboard memoizes this in a computed) and pass them in.
export function buildBadnessPools(activeDrivers) {
  const pools = {}
  const keys = new Set(FLAG_METRICS.map(m => m.dataKey))
  keys.forEach(dataKey => {
    pools[dataKey] = activeDrivers
      .map(d => d[dataKey])
      .filter(v => v != null)
      .sort((a, b) => a - b)
  })
  return pools
}

// Where a driver's value on `dataKey` ranks among the active fleet, expressed as a
// "badness" percentile: 100 = the single worst driver in the fleet on this metric,
// 0 = the best. Ties share a rank rather than double-counting, so a metric where
// most drivers cluster at the same value (e.g. many drivers at 0 harsh braking)
// doesn't inflate the flagged count for everyone at that value.
export function metricBadnessPercentile(d, dataKey, higherBetter, pools, activeDrivers) {
  const value = d[dataKey]
  if (value == null) return null
  // Prefer the memoized pool; fall back to building it inline from activeDrivers
  // so the function stays self-contained for direct unit-test calls.
  const sorted = (pools && pools[dataKey]) ||
    (activeDrivers || []).map(x => x[dataKey]).filter(v => v != null).sort((a, b) => a - b)
  if (!sorted || sorted.length < 2) return null
  // Binary search for the [lo, hi] span of entries equal to value; ties share a
  // rank rather than double-counting (many drivers at 0 harsh braking shouldn't
  // all flag). sorted is pre-sorted, so this is O(log N) instead of O(N log N).
  let lo = 0
  let hiBound = sorted.length
  while (lo < hiBound) {
    const mid = (lo + hiBound) >> 1
    if (sorted[mid] < value) lo = mid + 1
    else hiBound = mid
  }
  const first = lo
  if (first >= sorted.length || sorted[first] !== value) return null
  let hi = first
  while (hi + 1 < sorted.length && sorted[hi + 1] === value) hi++
  const idx = (first + hi) / 2
  const p = (idx / (sorted.length - 1)) * 100
  return higherBetter ? 100 - p : p
}

export function getDriverFlags(d, { pools, activeDrivers, thresholds }) {
  if (!isActiveDriver(d)) return []
  const flags = []
  FLAG_METRICS.forEach(m => {
    const value = d[m.dataKey]
    if (value == null) return
    if (m.dvirGate && !d.total_dvir_count) return
    // Gate: a metric can only flag if it's genuinely in the warning/danger band
    // per the tenant's own Conditional Coloring thresholds — a value that reads
    // 'text-success' there (e.g. an 85 safety score against a good:80 threshold)
    // must never flag just because the rest of the fleet happens to be clustered
    // even higher this period. Beyond the gate, the absolute band also caps how
    // severe a flag can be: 'critical' additionally requires the value to be in
    // the worst (danger) band, not merely warning — a driver can't be called
    // "critical" on a metric that isn't actually bad, no matter how poorly it
    // ranks against an unusually strong fleet this period.
    const fieldClass = getFieldClass(m.thresholdKey, value, thresholds)
    if (fieldClass !== 'text-warning' && fieldClass !== 'text-danger') return
    const badnessPct = metricBadnessPercentile(d, m.dataKey, m.higherBetter, pools, activeDrivers)
    if (badnessPct == null) return
    if (fieldClass === 'text-danger' && badnessPct >= FLAG_BADNESS_CRITICAL_PCT) {
      flags.push({ key: m.dataKey, label: m.label, severity: 'critical', value, note: m.note('critical', value) })
    } else if (badnessPct >= FLAG_BADNESS_WARNING_PCT) {
      flags.push({ key: m.dataKey, label: m.label, severity: 'warning', value, note: m.note('warning', value) })
    }
  })
  return flags.sort((a, b) => (a.severity === 'critical' && b.severity !== 'critical') ? -1 : 1)
}

// Classifies a driver into exactly one bucket, shared by chip counts, chip
// filters, and card accents so they always agree.
export function flaggedDriverCategory(d, ctx) {
  if (!isActiveDriver(d)) return 'lowActivity'
  const flags = getDriverFlags(d, ctx)
  if (flags.some(f => f.severity === 'critical')) return 'critical'
  if (flags.some(f => f.severity === 'warning')) return 'warning'
  return 'clean'
}

// Recognition candidates — the positive mirror of getDriverFlags, feeding the
// Coaching Portal's recognition lane (R1). Two reasons a driver qualifies:
//   1. Elite tier: the tenant's own compiled-score band says so.
//   2. Fleet best on a flagged metric: the best value among active drivers,
//      but only when that value also sits in the tenant's "good" band —
//      being the least-bad speeder in a bad fleet is not an award.
// Notes state the fact only, matching the flag-note convention. Requires a
// minimum pool so "fleet best" means something (best of three is noise).
const RECOGNITION_MIN_POOL = 5

export function getRecognitionCandidates(activeDrivers, thresholds) {
  const byDriver = new Map()
  const add = (d, reason) => {
    const key = d.driver_id || d.driver_name
    if (!byDriver.has(key)) byDriver.set(key, { key, driver: d, reasons: [] })
    byDriver.get(key).reasons.push(reason)
  }

  activeDrivers.forEach(d => {
    if (d.compiled_score_tier === 'Elite' && d.compiled_score != null) {
      add(d, {
        key: 'elite_tier', label: 'Elite tier', value: d.compiled_score,
        note: `Compiled score ${Math.round(d.compiled_score)} — Elite tier this period.`,
      })
    }
  })

  FLAG_METRICS.forEach(m => {
    const pool = activeDrivers.filter(d =>
      d[m.dataKey] != null && (!m.dvirGate || d.total_dvir_count))
    if (pool.length < RECOGNITION_MIN_POOL) return
    const values = pool.map(d => d[m.dataKey])
    const bestValue = m.higherBetter ? Math.max(...values) : Math.min(...values)
    // a distinction shared by several drivers is not a distinction —
    // crowning an arbitrary tie-holder would hand out unearned awards
    // (many drivers sit at 0 harsh braking or 100% DVIR)
    const holders = pool.filter(d => d[m.dataKey] === bestValue)
    if (holders.length !== 1) return
    const best = holders[0]
    if (getFieldClass(m.thresholdKey, bestValue, thresholds) !== 'text-success') return
    const rounded = Math.round(bestValue * 10) / 10
    add(best, {
      key: m.dataKey, label: `Fleet best — ${m.label}`, value: bestValue,
      note: `Best ${m.label.toLowerCase()} in the active fleet this period (${rounded}).`,
    })
  })

  return [...byDriver.values()].sort((a, b) => b.reasons.length - a.reasons.length)
}
